// Breadcrumbs for the global error boundary. When global-error.tsx renders,
// the whole React tree (providers included) is gone, so anything it needs to
// report must live outside React. sessionStorage is per-tab and survives both
// client-side navigation and full reloads within the tab.

const ROUTE_HISTORY_KEY = "mm-route-history";
const LAST_KNOWN_USER_KEY = "mm-last-known-user";
const MAX_ROUTES = 25;

export interface RouteBreadcrumb {
  url: string;
  at: string;
}

export interface LastKnownUser {
  userId: string;
  businessName: string;
  email: string;
  authEmail?: string;
}

export const recordRouteBreadcrumb = (url: string) => {
  try {
    const history = getRouteBreadcrumbs();
    if (history.length && history[history.length - 1].url === url) return;
    history.push({ url, at: new Date().toISOString() });
    sessionStorage.setItem(
      ROUTE_HISTORY_KEY,
      JSON.stringify(history.slice(-MAX_ROUTES))
    );
  } catch {
    // Never let telemetry break the app
  }
};

export const getRouteBreadcrumbs = (): RouteBreadcrumb[] => {
  try {
    const raw = sessionStorage.getItem(ROUTE_HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const recordLastKnownUser = (user: LastKnownUser) => {
  try {
    sessionStorage.setItem(LAST_KNOWN_USER_KEY, JSON.stringify(user));
  } catch {
    // Never let telemetry break the app
  }
};

export const getLastKnownUser = (): LastKnownUser | null => {
  try {
    const raw = sessionStorage.getItem(LAST_KNOWN_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// React logs the component stack of boundary-caught errors via console.error
// — the only place the owning component chain is named in production. Keep
// the last few entries in memory so error reports can include them.
const MAX_CONSOLE_ERRORS = 5;
const recentConsoleErrors: string[] = [];
let consoleTapInstalled = false;

export const installConsoleErrorTap = () => {
  if (consoleTapInstalled || typeof window === "undefined") return;
  consoleTapInstalled = true;
  try {
    const original = console.error;
    console.error = (...args: unknown[]) => {
      try {
        const text = args
          .map((a) =>
            typeof a === "string"
              ? a
              : a instanceof Error
                ? a.stack || a.message
                : String(a)
          )
          .join(" ")
          .slice(0, 4000);
        recentConsoleErrors.push(`${new Date().toISOString()} ${text}`);
        if (recentConsoleErrors.length > MAX_CONSOLE_ERRORS) {
          recentConsoleErrors.shift();
        }
      } catch {}
      original.apply(console, args);
    };
  } catch {}
};

export const getRecentConsoleErrors = (): string[] => [...recentConsoleErrors];
